import i18n from '@/i18n/config';
import React from 'react';
import { useAtom } from 'jotai';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';

import { aiUsageHydratedAtom, aiUsageListParamsAtom } from '@/store/aiUsage';

export const AI_USAGE_FEATURE_OPTIONS = [
  { key: 'all', label: i18n.t('filters.allFeatures') },
  { key: 'segment_compile', label: i18n.t('aiUsage.segmentCompile') },
  { key: 'translate_test', label: i18n.t('aiUsage.translateTest') },
  { key: 'latency_test', label: i18n.t('aiUsage.latencyTest') },
  { key: 'session_narrative', label: i18n.t('aiUsage.sessionNarrative') }
] as const;

export const AI_USAGE_STATUS_OPTIONS = [
  { key: 'all', label: i18n.t('filters.allStatuses') },
  { key: 'success', label: i18n.t('enums.success') },
  { key: 'error', label: i18n.t('enums.error') },
  { key: 'failed', label: i18n.t('enums.failed') },
  { key: 'timeout', label: i18n.t('enums.timeout') }
] as const;

export type AiUsageFeatureFilter = (typeof AI_USAGE_FEATURE_OPTIONS)[number]['key'];
export type AiUsageStatusFilter = (typeof AI_USAGE_STATUS_OPTIONS)[number]['key'];

export interface AiUsageSearchFields {
  feature_type: AiUsageFeatureFilter;
  status: AiUsageStatusFilter;
  model_slug: string;
  organization: string;
  user_email: string;
}

export function useAiUsageFilters() {
  const [params, setParams] = useAtom(aiUsageListParamsAtom);
  const [, setHydrated] = useAtom(aiUsageHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    sort: parseAsString,
    'filter[feature_type]': parseAsString,
    'filter[status]': parseAsString,
    'filter[model_slug]': parseAsString,
    'filter[organization]': parseAsString,
    'filter[user_email]': parseAsString
  });

  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};
    if (queryParams['filter[feature_type]']) {
      filters.feature_type = queryParams['filter[feature_type]'] || undefined;
    }
    if (queryParams['filter[status]']) {
      filters.status = queryParams['filter[status]'] || undefined;
    }
    if (queryParams['filter[model_slug]']) {
      filters.model_slug = queryParams['filter[model_slug]'] || undefined;
    }
    if (queryParams['filter[organization]']) {
      filters.organization = queryParams['filter[organization]'] || undefined;
    }
    if (queryParams['filter[user_email]']) {
      filters.user_email = queryParams['filter[user_email]'] || undefined;
    }

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      per_page: queryParams.per_page,
      sort: queryParams.sort || undefined,
      filters
    }));

    setHydrated(true);
  }, [
    queryParams.page,
    queryParams.per_page,
    queryParams.sort,
    queryParams['filter[feature_type]'],
    queryParams['filter[status]'],
    queryParams['filter[model_slug]'],
    queryParams['filter[organization]'],
    queryParams['filter[user_email]'],
    setParams,
    setHydrated
  ]);

  const searchFields = React.useMemo<AiUsageSearchFields>(
    () => ({
      feature_type: (queryParams['filter[feature_type]'] as AiUsageFeatureFilter) || 'all',
      status: (queryParams['filter[status]'] as AiUsageStatusFilter) || 'all',
      model_slug: queryParams['filter[model_slug]'] || '',
      organization: queryParams['filter[organization]'] || '',
      user_email: queryParams['filter[user_email]'] || ''
    }),
    [
      queryParams['filter[feature_type]'],
      queryParams['filter[status]'],
      queryParams['filter[model_slug]'],
      queryParams['filter[organization]'],
      queryParams['filter[user_email]']
    ]
  );

  const setSearchFields = React.useCallback(
    (updater: (prev: AiUsageSearchFields) => AiUsageSearchFields) => {
      const next = updater(searchFields);
      setQueryParams({
        'filter[feature_type]': next.feature_type === 'all' ? null : next.feature_type,
        'filter[status]': next.status === 'all' ? null : next.status,
        'filter[model_slug]': next.model_slug || null,
        'filter[organization]': next.organization || null,
        'filter[user_email]': next.user_email || null,
        page: 1
      });
    },
    [searchFields, setQueryParams]
  );

  const clearFilters = React.useCallback(() => {
    setQueryParams({
      'filter[feature_type]': null,
      'filter[status]': null,
      'filter[model_slug]': null,
      'filter[organization]': null,
      'filter[user_email]': null,
      page: 1
    });
  }, [setQueryParams]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      setQueryParams({ page });
    },
    [setQueryParams]
  );

  const handlePerPageChange = React.useCallback(
    (perPage: number) => {
      setQueryParams({ per_page: perPage, page: 1 });
    },
    [setQueryParams]
  );

  const sortParam = params.sort;
  const setSortParam = React.useCallback(
    (sortValue: string | null) => {
      setQueryParams({ sort: sortValue || null, page: 1 });
    },
    [setQueryParams]
  );

  const hasAnyFilter = Boolean(
    queryParams['filter[feature_type]'] ||
      queryParams['filter[status]'] ||
      queryParams['filter[model_slug]'] ||
      queryParams['filter[organization]'] ||
      queryParams['filter[user_email]']
  );

  return {
    params,
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    handlePerPageChange,
    hasAnyFilter,
    sortParam,
    setSortParam
  };
}
